import { match, P } from 'ts-pattern';
import { z } from 'zod';
import { zfd } from 'zod-form-data';
import api from '~/utils/api';
import { draftRegistrationToCreate } from '~/utils/registration';
import {
  makeRegistrationAction,
  makeRegistrationLoader,
  redirect,
} from '~/utils/routing';

export const SEARCH_PARAM = 'q';

type Context = NonNullable<
  Parameters<Parameters<typeof makeRegistrationLoader>[0]>[0]['context']
>;
type Settings = Context['settings'];

const pickProductTheme = (theme: Settings['theme']) => ({
  identifyProductUrl: theme.identifyProductUrl,
  identifyProductFormPlaceholder:
    theme.identifyProductFormPlaceholder ?? 'Search for your product',
  identifyProductUrlButtonText:
    theme.identifyProductUrlButtonText ?? 'Identify Your Product',
  identifyProductDetailText:
    theme.identifyProductDetailText ??
    'If you need help identifying your product, please contact us.',
});

const searchProducts = ({
  draft,
  storefrontId,
  request,
}: {
  draft: NonNullable<Context['registrations']['draft']>;
  storefrontId: Settings['storefrontId'];
  request: Request;
}) => {
  const searchTerm = new URLSearchParams(new URL(request.url).search).get(
    SEARCH_PARAM,
  );

  return searchTerm ?
      api.fetchRegistrationProducts({
        params: { storefrontId },
        query: {
          searchTerm,
          storeSalesChannelName: draft.storeSalesChannel.name,
          estimatedPurchaseDate: draft.estimatedPurchaseDate,
        },
      })
    : Promise.resolve([]);
};

export const { loader, useLoaderData } = makeRegistrationLoader(
  ({
    context: {
      registrations: { draft },
      settings: { storefrontId, theme },
    },
    params,
    request,
  }) =>
    !draft ?
      redirect(`/${params.store ?? ''}`)
    : searchProducts({ draft, storefrontId, request }).then((products) => ({
        products,
        ...pickProductTheme(theme),
      })),
);

export const FORM_KEY = 'variants';
const formSchema = zfd.formData({
  [FORM_KEY]: zfd.repeatable(
    z
      .array(
        z.object({
          id: zfd.numeric(),
          quantity: zfd.numeric(z.number().min(1)),
        }),
      )
      .min(1),
  ),
});

export const { action, useActionResult } = makeRegistrationAction(
  ({ context, formData, params }, appStore) =>
    match({
      draft: context.registrations.draft,
      formDataResult: formSchema.safeParse(formData),
    })
      .with({ draft: P.nullish }, () => redirect(`/${params.store ?? ''}`))
      .with(
        { formDataResult: { success: false } },
        () =>
          ({
            ok: false,
            message: 'Invalid form data',
          }) as const,
      )
      .with(
        { draft: P.not(P.nullish), formDataResult: { success: true } },
        ({ draft, formDataResult }) =>
          api
            .createRegistration({
              params: { storefrontId: context.settings.storefrontId },
              body: draftRegistrationToCreate(
                draft,
                formDataResult.data[FORM_KEY],
              ),
            })
            .then((registration) => {
              appStore.dispatch(
                appStore.actions.registrations.setRegistration(registration),
              );

              return redirect(`../overview`);
            }),
      )
      .otherwise(() => new Error('Unexpected state in selectProducts action')),
);
